// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { Box, Tab } from '@mui/material';
// Components, Layouts, Pages
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
import { BaseTabList, Loading } from '~/components/common';
import { TimeClockInformationTemplate, TimeClockNoteTemplate } from '~/pages/template';
// Others
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { IBaseTabItem } from '~/utils/interface/common';
import { EMPTY_STRING } from '~/utils/constants/common';
import { TimeClockDetailTabsEnum, RoleEnum } from '~/utils/enum/common';
import { StorageEnum } from '~/utils/enum/base';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getTimeClockDetails } from '~/thunks/timeClock/timeClockThunk';
// Styles, images, icons
import styles from './TimeClockDetail.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const TimeClockDetail = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/timeClock', 'common/base']);
  const { hash } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: timeClockId } = useParams();

  const getMenuItems = useCallback(() => {
    const items: Array<IBaseTabItem> = [
      { label: TimeClockDetailTabsEnum.INFORMATION, component: <TimeClockInformationTemplate /> },
    ];

    items.push({ label: TimeClockDetailTabsEnum.NOTES, component: <TimeClockNoteTemplate /> });

    setTabItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeClockId]);

  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [tabKey, setTabKey] = useState<string>('information');
  const [tabItems, setTabItems] = useState<IBaseTabItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (hash) setTabKey(hash.slice(1));
  }, [hash]);

  useEffect(() => {
    getMenuItems();
  }, [getMenuItems]);

  useEffect(() => {
    handleGetTimeClockDetails();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeClockId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleBackToCustomers = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.timeClock);
        break;
      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.timeClock);
        break;
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    navigate(`#${newValue}`);
  };

  const renderTabContent = () => {
    const item = tabItems.find((tabItem: IBaseTabItem) => {
      return tabKey === tabItem.label;
    });

    return item?.component;
  };

  const handleGetTimeClockDetails = () => {
    if (!timeClockId) return;

    setIsLoading(true);

    dispatch(getTimeClockDetails(timeClockId))
      .unwrap()
      .then((res) => {})
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function

  return (
    <ChildrenPage
      id='timeClockDetailPage'
      title={t('time_clock_detail_title')}
      onBackToPreviousPage={handleBackToCustomers}
    >
      <>
        <BaseTabList value={tabKey} onChange={handleTabChange}>
          {tabItems.map((item, index) => (
            <Tab
              value={item.label}
              key={index}
              label={t(`tab_label_${item.label}`)}
              className={cx('tabItem')}
            />
          ))}
        </BaseTabList>

        <Box component={'div'} className={cx('tabContent')}>
          {renderTabContent()}
        </Box>

        {isLoading && <Loading />}
      </>
    </ChildrenPage>
  );
};

export default TimeClockDetail;
